import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  DollarSign, TrendingUp, TrendingDown, Users, Calendar, Download,
  BarChart2, PieChart, Clock, Star, FileText, RefreshCw, CalendarDays, Target
} from 'lucide-react';
import { Button } from '../ui/button';
import { analyticsAPI } from '../../services/api';
import { toast } from 'sonner';
import EstimateAccuracyDashboard from './EstimateAccuracyDashboard';

const ReportsTab = () => {
  const [activeSubTab, setActiveSubTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('month');
  const [customRange, setCustomRange] = useState({ start: '', end: '' });
  const [showCustomRange, setShowCustomRange] = useState(false);
  const [revenueData, setRevenueData] = useState(null);
  const [workerPerformance, setWorkerPerformance] = useState(null);
  const [popularServices, setPopularServices] = useState(null);
  const [customerInsights, setCustomerInsights] = useState(null);

  useEffect(() => {
    if (period !== 'custom') {
      fetchAnalytics();
    }
  }, [period]);

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const params = { period };
      if (period === 'custom' && customRange.start && customRange.end) {
        params.start_date = customRange.start;
        params.end_date = customRange.end;
      }
      
      const [revenue, workers, services, customers] = await Promise.all([
        analyticsAPI.getRevenue(params),
        analyticsAPI.getWorkerPerformance(params),
        analyticsAPI.getPopularServices(params),
        analyticsAPI.getCustomerInsights(params)
      ]);
      
      setRevenueData(revenue.data);
      setWorkerPerformance(workers.data);
      setPopularServices(services.data);
      setCustomerInsights(customers.data);
    } catch (error) {
      toast.error('Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  const handleCustomRangeApply = () => {
    if (customRange.start && customRange.end) {
      fetchAnalytics();
      setShowCustomRange(false);
    } else {
      toast.error('Please select both start and end dates');
    }
  };

  const handleExport = async (type) => {
    try {
      const response = type === 'revenue' 
        ? await analyticsAPI.exportRevenue({ format: 'csv' })
        : await analyticsAPI.exportWorkers({ format: 'csv' });
      
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${type}_report.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success(`${type} report exported`);
    } catch (error) {
      toast.error('Failed to export report');
    }
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} data-testid="reports-tab">
      {/* Sub-Tab Navigation */}
      <div className="flex gap-2 mb-6 border-b border-[var(--border-primary)] pb-4">
        <Button
          variant={activeSubTab === 'overview' ? 'default' : 'ghost'}
          onClick={() => setActiveSubTab('overview')}
          className={activeSubTab === 'overview' ? 'bg-[var(--primary)] text-black' : ''}
          data-testid="reports-overview-tab"
        >
          <BarChart2 className="w-4 h-4 mr-2" />
          Overview
        </Button>
        <Button
          variant={activeSubTab === 'accuracy' ? 'default' : 'ghost'}
          onClick={() => setActiveSubTab('accuracy')}
          className={activeSubTab === 'accuracy' ? 'bg-[var(--primary)] text-black' : ''}
          data-testid="reports-accuracy-tab"
        >
          <Target className="w-4 h-4 mr-2" />
          Time Accuracy
        </Button>
      </div>

      {/* Estimate Accuracy Dashboard */}
      {activeSubTab === 'accuracy' && (
        <EstimateAccuracyDashboard />
      )}

      {/* Overview Analytics */}
      {activeSubTab === 'overview' && loading && (
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="w-8 h-8 animate-spin text-[var(--primary)]" />
        </div>
      )}
      
      {activeSubTab === 'overview' && !loading && (
        <>
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
            <h1 className="text-2xl lg:text-3xl font-bold font-['Cormorant_Garamond']">Analytics & Reports</h1>
        
        <div className="flex flex-wrap items-center gap-3">
          {/* Period Selector */}
          <div className="flex flex-wrap bg-[var(--background-subtle)] rounded-lg p-1 gap-1">
            {[
              { id: 'day', label: 'Today' },
              { id: 'week', label: 'Week' },
              { id: 'month', label: 'Month' },
              { id: 'quarter', label: 'Quarter' },
              { id: 'year', label: 'Year' },
              { id: 'custom', label: 'Custom' }
            ].map(p => (
              <button
                key={p.id}
                onClick={() => {
                  setPeriod(p.id);
                  if (p.id === 'custom') setShowCustomRange(true);
                }}
                className={`px-3 py-1 rounded-md text-sm font-medium transition-all ${
                  period === p.id ? 'bg-[var(--background-paper)] shadow text-[var(--primary)]' : 'text-[var(--text-secondary)] hover:text-[var(--text-primary)]'
                }`}
              >
                {p.label}
              </button>
            ))}
          </div>
          
          {/* Custom Date Range */}
          {showCustomRange && (
            <div className="flex items-center gap-2 bg-[var(--background-paper)] border rounded-lg p-2">
              <input
                type="date"
                value={customRange.start}
                onChange={(e) => setCustomRange(prev => ({ ...prev, start: e.target.value }))}
                className="border rounded px-2 py-1 text-sm"
              />
              <span className="text-gray-400">to</span>
              <input
                type="date"
                value={customRange.end}
                onChange={(e) => setCustomRange(prev => ({ ...prev, end: e.target.value }))}
                className="border rounded px-2 py-1 text-sm"
              />
              <Button size="sm" onClick={handleCustomRangeApply}>Apply</Button>
            </div>
          )}
          
          <Button variant="outline" size="sm" onClick={fetchAnalytics}>
            <RefreshCw className="w-4 h-4 mr-1" /> Refresh
          </Button>
          
          <Button variant="outline" size="sm" onClick={() => handleExport('revenue')}>
            <Download className="w-4 h-4 mr-1" /> Export
          </Button>
        </div>
      </div>

      {/* Revenue Summary */}
      {revenueData && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <div className="card p-6">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-[var(--success)]/15 rounded-full flex items-center justify-center">
                <DollarSign className="w-5 h-5 text-[var(--success)]" />
              </div>
              <span className="text-sm text-[var(--text-muted)]">Total Revenue</span>
            </div>
            <p className="text-2xl font-bold text-[var(--success)]">${revenueData.summary.total_revenue.toLocaleString()}</p>
            <p className="text-xs text-[var(--text-muted)] mt-1">
              Pending: ${revenueData.summary.pending_revenue.toLocaleString()}
            </p>
          </div>

          <div className="card p-6">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-[#D4AF37]/10 rounded-full flex items-center justify-center">
                <Calendar className="w-5 h-5 text-[#D4AF37]" />
              </div>
              <span className="text-sm text-[var(--text-muted)]">Bookings</span>
            </div>
            <p className="text-2xl font-bold">{revenueData.summary.total_bookings}</p>
            <p className="text-xs text-[var(--text-muted)] mt-1">
              Completed: {revenueData.summary.completed_bookings}
            </p>
          </div>

          <div className="card p-6">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-[#F4C2C2]/15 rounded-full flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-[#F4C2C2]" />
              </div>
              <span className="text-sm text-[var(--text-muted)]">Avg Booking</span>
            </div>
            <p className="text-2xl font-bold">${revenueData.summary.average_booking_value.toFixed(2)}</p>
            <p className="text-xs text-[var(--text-muted)] mt-1">
              Completion: {revenueData.summary.completion_rate}%
            </p>
          </div>

          <div className="card p-6">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
                <PieChart className="w-5 h-5 text-amber-600" />
              </div>
              <span className="text-sm text-[var(--text-muted)]">Top Service</span>
            </div>
            {Object.entries(revenueData.revenue_by_service).length > 0 ? (
              <>
                <p className="text-lg font-bold truncate">
                  {Object.entries(revenueData.revenue_by_service).sort((a, b) => b[1] - a[1])[0]?.[0] || 'N/A'}
                </p>
                <p className="text-xs text-[var(--text-muted)] mt-1">
                  ${Object.entries(revenueData.revenue_by_service).sort((a, b) => b[1] - a[1])[0]?.[1]?.toFixed(2) || 0}
                </p>
              </>
            ) : (
              <p className="text-lg font-bold text-gray-400">No data</p>
            )}
          </div>
        </div>
      )}

      {/* Revenue by Service Type */}
      {revenueData && Object.keys(revenueData.revenue_by_service).length > 0 && (
        <div className="card p-6 mb-8">
          <h3 className="font-bold mb-4 flex items-center gap-2">
            <BarChart2 className="w-5 h-5" /> Revenue by Service Type
          </h3>
          <div className="space-y-3">
            {Object.entries(revenueData.revenue_by_service)
              .sort((a, b) => b[1] - a[1])
              .slice(0, 5)
              .map(([type, amount]) => {
                const total = Object.values(revenueData.revenue_by_service).reduce((a, b) => a + b, 0);
                const percentage = total > 0 ? (amount / total * 100) : 0;
                return (
                  <div key={type}>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="capitalize">{type.replace('_', ' ')}</span>
                      <span className="font-medium">${amount.toFixed(2)} ({percentage.toFixed(1)}%)</span>
                    </div>
                    <div className="h-2 bg-[var(--background-subtle)] rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-[var(--primary)] rounded-full transition-all"
                        style={{ width: `${percentage}%` }}
                      />
                    </div>
                  </div>
                );
              })}
          </div>
        </div>
      )}

      <div className="grid lg:grid-cols-2 gap-6 mb-8">
        {/* Top Workers */}
        {workerPerformance && workerPerformance.workers.length > 0 && (
          <div className="card p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold flex items-center gap-2">
                <Users className="w-5 h-5" /> Top Workers
              </h3>
              <Button variant="ghost" size="sm" onClick={() => handleExport('workers')}>
                <Download className="w-4 h-4" />
              </Button>
            </div>
            <div className="space-y-4">
              {workerPerformance.workers.slice(0, 5).map((worker, index) => (
                <div key={worker.worker_id} className="flex items-center gap-4">
                  <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                    index === 0 ? 'bg-[var(--warning)]/15 text-yellow-700' :
                    index === 1 ? 'bg-[var(--background-subtle)] text-[var(--text-primary)]' :
                    index === 2 ? 'bg-amber-100 text-amber-700' :
                    'bg-[var(--background-subtle)] text-[var(--text-secondary)]'
                  }`}>
                    {index + 1}
                  </span>
                  {worker.worker_photo ? (
                    <img src={worker.worker_photo} alt="" className="w-10 h-10 rounded-full object-cover" />
                  ) : (
                    <div className="w-10 h-10 bg-[var(--background-subtle)] rounded-full flex items-center justify-center">
                      <Users className="w-5 h-5 text-[var(--text-muted)]" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate">{worker.worker_name}</p>
                    <div className="flex items-center gap-2 text-xs text-[var(--text-muted)]">
                      <span>{worker.metrics.total_jobs} jobs</span>
                      <span>•</span>
                      <span className="flex items-center gap-0.5">
                        <Star className="w-3 h-3 fill-yellow-400 text-yellow-400" />
                        {worker.metrics.average_rating.toFixed(1)}
                      </span>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-[var(--success)]">${worker.metrics.total_revenue_generated.toFixed(0)}</p>
                    <p className="text-xs text-[var(--text-muted)]">{worker.metrics.total_hours}h</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Popular Services */}
        {popularServices && popularServices.services.length > 0 && (
          <div className="card p-6">
            <h3 className="font-bold flex items-center gap-2 mb-4">
              <TrendingUp className="w-5 h-5" /> Popular Services
            </h3>
            <div className="space-y-3">
              {popularServices.services.slice(0, 5).map((service, index) => (
                <div key={service.service_name} className="flex items-center justify-between py-2 border-b last:border-0">
                  <div className="flex items-center gap-3">
                    <span className="text-lg font-bold text-gray-300">#{index + 1}</span>
                    <div>
                      <p className="font-medium">{service.service_name}</p>
                      <p className="text-xs text-[var(--text-muted)]">
                        {service.completed_count} completed • {service.completion_rate}% success
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-bold">{service.booking_count} bookings</p>
                    <p className="text-xs text-[var(--success)]">${service.revenue.toFixed(0)}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Customer Insights */}
      {customerInsights && (
        <div className="card p-6">
          <h3 className="font-bold flex items-center gap-2 mb-4">
            <Users className="w-5 h-5" /> Customer Insights
          </h3>
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
            <div className="text-center p-4 bg-[var(--background-subtle)] rounded-lg">
              <p className="text-2xl font-bold">{customerInsights.summary.total_customers}</p>
              <p className="text-xs text-[var(--text-muted)]">Total Customers</p>
            </div>
            <div className="text-center p-4 bg-[var(--success)]/10 rounded-lg">
              <p className="text-2xl font-bold text-[var(--success)]">{customerInsights.summary.new_customers_in_period}</p>
              <p className="text-xs text-[var(--text-muted)]">New This Period</p>
            </div>
            <div className="text-center p-4 bg-[#D4AF37]/10 rounded-lg">
              <p className="text-2xl font-bold text-[#D4AF37]">{customerInsights.summary.active_customers_in_period}</p>
              <p className="text-xs text-[var(--text-muted)]">Active</p>
            </div>
            <div className="text-center p-4 bg-[#F4C2C2]/10 rounded-lg">
              <p className="text-2xl font-bold text-[#F4C2C2]">{customerInsights.summary.returning_customers}</p>
              <p className="text-xs text-[var(--text-muted)]">Returning</p>
            </div>
            <div className="text-center p-4 bg-[var(--warning)]/10 rounded-lg">
              <p className="text-2xl font-bold text-amber-600">{customerInsights.summary.average_bookings_per_customer}</p>
              <p className="text-xs text-[var(--text-muted)]">Avg Bookings</p>
            </div>
          </div>

          {/* Top Customers */}
          {customerInsights.top_customers.length > 0 && (
            <div className="mt-6">
              <h4 className="font-medium mb-3">Top Customers by Revenue</h4>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-2">Customer</th>
                      <th className="text-center py-2">Bookings</th>
                      <th className="text-right py-2">Total Spent</th>
                    </tr>
                  </thead>
                  <tbody>
                    {customerInsights.top_customers.slice(0, 5).map(customer => (
                      <tr key={customer.customer_id} className="border-b last:border-0">
                        <td className="py-2">{customer.customer_name}</td>
                        <td className="text-center py-2">{customer.booking_count}</td>
                        <td className="text-right py-2 font-bold text-[var(--success)]">
                          ${customer.total_spent.toFixed(2)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}
        </>
      )}
    </motion.div>
  );
};

export default ReportsTab;
